/*
 * Branch Storefront — ties the shopper's branch to what they see and buy.
 *
 * Rides on the existing branch picker (pickup-location.js / window.PickupLocation)
 * and on the theme's own facet filtering (facet-filters.js). The branch comes
 * from the picker; this script only:
 *
 *   1. writes it to the cart as the attribute `_bsf_location` (a Location GID),
 *      which the Branch Storefront checkout rules read to deliver and collect
 *      only from that branch;
 *   2. stamps it on every add-to-cart as the line property `_bsf_location`
 *      ("Buy it now" starts a fresh cart that never sees cart attributes);
 *   3. filters collections and search SERVER-SIDE with the storefront filter
 *      `<param>=<branch slug>`, so counts, pagination and facets are real:
 *        - links are rewritten at tap time (native navigation — the AppMe
 *          webview blocks programmatic navigation, so nothing here redirects);
 *        - a page opened without the filter is re-rendered in place through the
 *          theme's own facet-filters element, then the URL is replaced.
 *
 * Every cart carries a branch (owner decision 2026-09-22): when the shopper has
 * neither chosen one nor been geolocated, the picker's default branch (6th of
 * October, from pickup-locations.json) is applied — to the cart AND to
 * listings, so the catalogue shown matches what checkout will deliver.
 */
(function () {
  'use strict';

  var script = document.currentScript || document.querySelector('script[data-branch-storefront]');
  var PARAM = (script && script.getAttribute('data-filter-param')) || '';
  // false in languages where Shopify's search index does not serve the branch
  // filter yet: listings stay unfiltered, checkout pinning still applies.
  var FILTER_LISTINGS = !script || script.getAttribute('data-filter-listings') !== 'false';
  var ATTR = '_bsf_location';
  var COOKIE = 'bsf_location';
  var COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
  var GID = /^gid:\/\/shopify\/Location\/\d+$/;
  var LISTING_PATH = /^(\/[a-z]{2}(-[a-zA-Z]{2})?)?\/(collections\/[^/?#]+|search)\/?$/;

  if (!PARAM) {
    console.warn('[branch-storefront] missing data-filter-param');
    done();
    return;
  }

  var current = { id: null, slug: null };

  function done() {
    document.documentElement.classList.remove('bsf-pending');
  }

  function writeCookie(value) {
    document.cookie = COOKIE + '=' + encodeURIComponent(value) +
      '; path=/; max-age=' + COOKIE_MAX_AGE + '; SameSite=Lax';
  }

  function isListing(url) {
    return url.origin === window.location.origin && LISTING_PATH.test(url.pathname);
  }

  function withBranch(url, slug) {
    if (url.searchParams.get(PARAM) !== slug) {
      // Page N of another branch's (or the unfiltered) results is meaningless.
      url.searchParams.delete('page');
    }
    url.searchParams.set(PARAM, slug);
    return url;
  }

  // --- 1. cart attribute ------------------------------------------------------

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var SYNC_KEY = 'bsf_synced_location';

  // Keeps the cart on the shopper's branch, only for a cart with items (no cart is
  // created for visitors who never add anything; the line property covers the
  // first add and the next page syncs):
  //  - the attribute, which the checkout rules read first;
  //  - every line's `_bsf_location` property. Shopify merges repeat adds of a
  //    variant only when the line properties match, so a line still tagged with
  //    the previous branch made the same item appear on two lines after a switch.
  //    One request per stale line; line order and quantity are kept.
  function syncCart(id) {
    if (!id) return;
    try { if (sessionStorage.getItem(SYNC_KEY) === id) return; } catch (_) {}
    var json = { 'Content-Type': 'application/json', Accept: 'application/json' };
    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.item_count) return;
        var steps = Promise.resolve(true);
        if (!cart.attributes || cart.attributes[ATTR] !== id) {
          var attributes = {};
          attributes[ATTR] = id;
          steps = fetch(root + 'cart/update.js', {
            method: 'POST', headers: json, body: JSON.stringify({ attributes: attributes })
          }).then(function (r) { return r.ok; });
        }
        var stale = (cart.items || []).filter(function (item) {
          return item.properties && item.properties[ATTR] && item.properties[ATTR] !== id;
        });
        stale.forEach(function (item) {
          steps = steps.then(function (ok) {
            var properties = {};
            Object.keys(item.properties).forEach(function (k) { properties[k] = item.properties[k]; });
            properties[ATTR] = id;
            return fetch(root + 'cart/change.js', {
              method: 'POST', headers: json,
              body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: properties })
            }).then(function (r) { return ok && r.ok; });
          });
        });
        return steps.then(function (ok) {
          if (ok) { try { sessionStorage.setItem(SYNC_KEY, id); } catch (_) {} }
          // Line keys change with their properties: re-render anything that holds them.
          if (stale.length) document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        });
      })
      .catch(function (err) { console.warn('[branch-storefront] cart sync failed', err); });
  }

  // --- 2. line property on add-to-cart ---------------------------------------

  function stamp(form) {
    // getAttribute, not form.action: an input named "action" shadows the property.
    var action = form && form.getAttribute ? form.getAttribute('action') || '' : '';
    if (!current.id || action.indexOf('/cart/add') === -1) return;
    var name = 'properties[' + ATTR + ']';
    var input = form.querySelector('input[name="' + name + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = current.id;
  }

  // Capture phase: runs before the theme's own submit/click handlers read FormData.
  document.addEventListener('submit', function (evt) {
    stamp(evt.target);
    try { sessionStorage.removeItem(SYNC_KEY); } catch (_) {}
  }, true);
  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest && evt.target.closest('button, input[type="submit"]');
    if (btn && btn.form) stamp(btn.form);
  }, true);

  // --- 3a. tap-time link rewriting -------------------------------------------

  function rewrite(evt) {
    if (!current.slug || !FILTER_LISTINGS) return;
    var a = evt.target.closest && evt.target.closest('a[href]');
    if (!a || a.target === '_blank') return;
    var url;
    try { url = new URL(a.href, window.location.href); } catch (_) { return; }
    if (!isListing(url) || url.searchParams.get(PARAM) === current.slug) return;
    // Links within the listing being viewed (pagination, "load more", facet
    // pills) are left to the page: if it is filtered they already carry the
    // branch; if it is not, rewriting them would mix filtered pages into an
    // unfiltered grid. Facet links are read by facet-filters.js from the form.
    if (url.pathname === window.location.pathname && !a.classList.contains('js-clear-all')) return;
    a.href = withBranch(url, current.slug).toString();
  }
  document.addEventListener('pointerdown', rewrite, true);
  document.addEventListener('keydown', function (evt) { if (evt.key === 'Enter') rewrite(evt); }, true);

  // --- 3b. in-place filtering of the current listing -------------------------

  function syncFacetInputs() {
    document.querySelectorAll('input[data-branch-filter-input]').forEach(function (input) {
      input.value = FILTER_LISTINGS ? current.slug || '' : '';
    });
  }

  function whenFacetsReady(timeoutMs) {
    // facet-filters.js is a deferred BODY script and may run after this one:
    // the element can exist before its class (and applyFilters) is defined.
    if (!document.querySelector('facet-filters')) return Promise.resolve(null);
    return Promise.race([
      customElements.whenDefined('facet-filters').then(function () { return document.querySelector('facet-filters'); }),
      new Promise(function (resolve) { setTimeout(function () { resolve(null); }, timeoutMs); })
    ]);
  }

  var filtering = false;
  var rerun = false; // a branch change arrived while a re-render was running
  function filterCurrentPage() {
    var here = new URL(window.location.href);
    // Where listings are not branch-filtered (e.g. Arabic until Shopify serves
    // the filter there), a URL that still carries it (language switch from an
    // English listing) is re-rendered WITHOUT it rather than left empty.
    var strip = !FILTER_LISTINGS && here.searchParams.has(PARAM);
    if (filtering || !isListing(here) || (!strip && (!FILTER_LISTINGS || !current.slug || here.searchParams.get(PARAM) === current.slug))) {
      if (filtering) rerun = true;
      else done();
      return;
    }
    filtering = true;
    rerun = false;
    var startSearch = window.location.search;
    var target;
    if (strip) {
      target = here;
      target.searchParams.delete(PARAM);
      target.searchParams.delete('page');
    } else {
      target = withBranch(here, current.slug);
    }
    var params = target.searchParams.toString();

    whenFacetsReady(4000).then(function (facets) {
      if (!facets || typeof facets.applyFilters !== 'function') {
        // No facet element on this template (filters switched off): nothing to
        // re-render in place. Links are still rewritten, so the next listing
        // the shopper opens is filtered.
        return;
      }
      // applyFilters swallows its own errors, so success is judged by the
      // theme's "updated" event, not by the promise resolving.
      var rendered = false;
      var onUpdated = function () { rendered = true; };
      document.addEventListener('on:facet-filters:updated', onUpdated, { once: true });
      return Promise.resolve(facets.applyFilters(params, null, false)).then(function () {
        document.removeEventListener('on:facet-filters:updated', onUpdated);
        // Only claim the filter in the URL if the grid really is filtered, and
        // never overwrite a URL the shopper changed (a facet tap) meanwhile.
        if (rendered && window.location.search === startSearch) {
          window.history.replaceState({ searchParams: params }, '', target.pathname + (params ? '?' + params : '') + target.hash);
        }
      });
    })
      .catch(function (err) { console.warn('[branch-storefront] in-place filter failed', err); })
      .then(function () {
        filtering = false;
        if (rerun) { rerun = false; filterCurrentPage(); return; }
        done();
      });
  }

  // The banner heading is a separate section that facet re-renders never touch:
  // copy the filtered count from the re-rendered results into it.
  function syncHeadingCount() {
    var source = document.querySelector('#filter-results [data-results-count]');
    var heading = document.querySelector('.collection-banner__count');
    if (!source || !heading) return;
    var count = source.getAttribute('data-results-count');
    if (!/^\d+$/.test(count)) return;
    heading.textContent = heading.textContent.replace(/\d[\d,.\u066C]*/, count);
  }

  // snippets/branch-listing-notice.liquid prints the slug; show the store's
  // localised name instead.
  function nameListingNotices() {
    if (!window.PickupLocation) return;
    var isArabic = (document.documentElement.getAttribute('lang') || '').indexOf('ar') === 0;
    var bySlug = {};
    window.PickupLocation.list().forEach(function (loc) { if (loc.slug) bySlug[loc.slug] = loc; });
    document.querySelectorAll('[data-bsf-store-name]').forEach(function (el) {
      var loc = bySlug[el.getAttribute('data-slug')];
      if (loc) el.textContent = (isArabic && loc.name_ar) ? loc.name_ar : loc.name;
    });
  }
  document.addEventListener('on:facet-filters:updated', nameListingNotices);

  document.addEventListener('on:facet-filters:updated', function () {
    syncFacetInputs();
    syncHeadingCount();
  });

  // --- 4. product page: the selected variant at the shopper's branch --------------
  //
  // snippets/branch-variant-stock.liquid prints { variants: { id: [slugs] } }
  // inside each <product-form>. When the branch does not stock the selected
  // variant, "Add to cart" says so and Buy-now is hidden; checkout would refuse
  // delivery from the branch anyway, this just says it where the shopper is.

  function branchName() {
    var loc = current.id && window.PickupLocation ? window.PickupLocation.findById(current.id) : null;
    if (!loc) return '';
    var arabic = (document.documentElement.getAttribute('lang') || '').indexOf('ar') === 0;
    return (arabic && loc.name_ar) || loc.name || '';
  }

  // Option values (sizes, colours) whose variants the branch does not stock,
  // given what is selected in the other options: struck through with the
  // theme's own sold-out style, still selectable - picking one shows "Not
  // available at X" and the nearby branches that have it. A variant not indexed yet
  // (null) counts as stocked. If the preselected value is one of them, the
  // first value the branch has is selected instead - once per product form, so
  // two options can never bounce the selection back and forth.
  function optionValue(el) {
    return el.tagName === 'INPUT' ? el.value : (el.getAttribute('data-value') || '');
  }

  function markOptions(productForm, data) {
    if (!data.options) return;
    var scope = productForm.closest('.shopify-section, quick-add-drawer, .quick-add') || document;
    var picker = scope.querySelector('variant-picker');
    if (!picker) return;
    var selectors = picker.querySelectorAll('.option-selector[data-index]');
    var selected = {};
    selectors.forEach(function (sel) {
      var on = sel.querySelector('.js-option:is(:checked, [aria-selected="true"])');
      selected[sel.getAttribute('data-index')] = on ? optionValue(on) : null;
    });
    var ids = Object.keys(data.options);
    selectors.forEach(function (sel) {
      var pos = Number(sel.getAttribute('data-index')) - 1;
      sel.querySelectorAll('.js-option').forEach(function (el) {
        var value = optionValue(el);
        if (!value) return;
        var candidates = ids.filter(function (id) {
          var opts = data.options[id] || [];
          if (opts[pos] !== value) return false;
          for (var key in selected) {
            var q = Number(key) - 1;
            if (q !== pos && selected[key] !== null && opts[q] !== selected[key]) return false;
          }
          return true;
        });
        var off = !!current.slug && candidates.length > 0 && candidates.every(function (id) {
          var slugs = data.variants[id];
          return Array.isArray(slugs) && slugs.indexOf(current.slug) === -1;
        });
        var marked = el.hasAttribute('data-bsf-off');
        if (off && !marked) {
          el.setAttribute('data-bsf-off', el.classList.contains('is-unavailable') ? 'had' : 'added');
          el.classList.add('is-unavailable');
        } else if (!off && marked) {
          if (el.getAttribute('data-bsf-off') === 'added') el.classList.remove('is-unavailable');
          el.removeAttribute('data-bsf-off');
        }
      });
    });
    if (productForm.hasAttribute('data-bsf-switched')) return;
    selectors.forEach(function (sel) {
      var on = sel.querySelector('.js-option:is(:checked, [aria-selected="true"])');
      if (!on || !on.hasAttribute('data-bsf-off')) return;
      var next = sel.querySelector('.js-option:not([data-bsf-off]):not(.is-unavailable):not([data-value=""])');
      if (!next) return;
      productForm.setAttribute('data-bsf-switched', '');
      if (next.tagName === 'INPUT') next.click();
      else if (next.closest('custom-select')) next.closest('custom-select').selectOption(next);
    });
  }

  // Nearest branches (from the chosen one - no location prompt on a product
  // page) that stock the selected variant: one tap switches through the picker,
  // which still checks the cart for items the new branch lacks.
  var NEARBY_MAX = 2;
  function kmBetween(a, b) {
    var rad = Math.PI / 180;
    var dLat = (b.lat - a.lat) * rad, dLng = (b.lng - a.lng) * rad;
    var h = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 12742 * Math.asin(Math.sqrt(h));
  }
  function hasCoords(loc) { return loc && isFinite(loc.lat) && isFinite(loc.lng); }

  function nearbyBranches(slugs) {
    if (!window.PickupLocation || !Array.isArray(slugs)) return [];
    var here = window.PickupLocation.findById(current.id);
    var stocked = window.PickupLocation.list().filter(function (loc) {
      return loc.slug && loc.slug !== current.slug && slugs.indexOf(loc.slug) !== -1;
    });
    if (hasCoords(here)) {
      stocked.sort(function (a, b) {
        var da = hasCoords(a) ? kmBetween(here, a) : Infinity;
        var db = hasCoords(b) ? kmBetween(here, b) : Infinity;
        return da - db;
      });
    }
    return stocked.slice(0, NEARBY_MAX);
  }

  function renderNearby(note, data, slugs) {
    var arabic = (document.documentElement.getAttribute('lang') || '').indexOf('ar') === 0;
    var labels = data.labels || {};
    note.innerHTML = '';
    var nearby = nearbyBranches(slugs);
    if (nearby.length) {
      var label = document.createElement('p');
      label.className = 'bsf-nearby__label';
      label.textContent = labels.nearby || '';
      note.appendChild(label);
      var row = document.createElement('div');
      row.className = 'bsf-nearby__list';
      nearby.forEach(function (loc) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'bsf-nearby__branch';
        chip.textContent = (arabic && loc.name_ar) || loc.name;
        chip.addEventListener('click', function () {
          if (window.PickupSelector) window.PickupSelector.choose(loc.id);
          else if (window.PickupLocation) window.PickupLocation.set(loc.id);
        });
        row.appendChild(chip);
      });
      note.appendChild(row);
    }
    var other = document.createElement('button');
    other.type = 'button';
    other.className = 'btn btn--secondary w-full mt-3';
    other.setAttribute('data-pickup-open', '');
    other.textContent = labels.choose || '';
    note.appendChild(other);
  }

  function applyProductForm(productForm) {
    var dataEl = productForm.querySelector('script[data-branch-stock]');
    var form = productForm.querySelector('form[action*="/cart/add"]');
    if (!dataEl || !form) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (_) { return; }
    try { markOptions(productForm, data); } catch (err) { console.warn('[branch-storefront] option marking failed', err); }
    var idInput = form.querySelector('[name="id"]');
    var button = form.querySelector('[name="add"]');
    if (!idInput || !button) return;

    var payment = form.querySelector('.shopify-payment-button');
    var note = productForm.querySelector('[data-branch-stock-note]');
    var slugs = data.variants && data.variants[idInput.value];
    var gatedByUs = button.hasAttribute('data-bsf-original');
    // The theme has already said no (sold out, preselection "Select option"):
    // leave its wording alone rather than add a dead-end "choose a store".
    var themeSaysNo = button.disabled && !gatedByUs;
    var notAtBranch = !themeSaysNo && !!current.slug && Array.isArray(slugs) && slugs.indexOf(current.slug) === -1;

    if (notAtBranch) {
      if (!gatedByUs) {
        button.setAttribute('data-bsf-original', button.disabled ? 'disabled' : 'enabled');
        button.setAttribute('data-bsf-original-text', button.textContent);
      }
      button.disabled = true;
      button.textContent = String(data.labels && data.labels.unavailable || '').replace('%STORE%', branchName());
      if (payment) { payment.hidden = true; payment.setAttribute('data-bsf-hidden', ''); }
      if (!note) {
        note = document.createElement('div');
        note.className = 'bsf-nearby mt-3';
        note.setAttribute('data-branch-stock-note', '');
        form.parentNode.insertBefore(note, form.nextSibling);
      }
      renderNearby(note, data, slugs);
      note.hidden = false;
      return;
    }

    // Not blocked. The theme replaces the button block on a variant change, so
    // the payment button and the note (outside that block) are restored on
    // their own marks, not on the button's.
    if (gatedByUs) {
      button.disabled = button.getAttribute('data-bsf-original') === 'disabled';
      button.textContent = button.getAttribute('data-bsf-original-text') || button.textContent;
      button.removeAttribute('data-bsf-original');
      button.removeAttribute('data-bsf-original-text');
    }
    if (payment && payment.hasAttribute('data-bsf-hidden')) {
      payment.hidden = false;
      payment.removeAttribute('data-bsf-hidden');
    }
    if (note) note.hidden = true;
  }

  function applyProductForms() {
    document.querySelectorAll('product-form').forEach(applyProductForm);
    labelCards();
  }

  // Product cards outside filtered listings (homepage and app-page hand-picked
  // lists, recently viewed, recommendations): a card the branch does not stock
  // stays, with a "Not available at X" label, so merchandising stays visible.
  // Its own Add to cart is hidden meanwhile: the product page disables the
  // button in the same case, and on mobile the cart icon sits over the label.
  function labelCards() {
    document.querySelectorAll('product-card[data-bsf-branches]').forEach(function (card) {
      var branches = (card.getAttribute('data-bsf-branches') || '').split(',');
      var missing = !!current.slug && branches.indexOf(current.slug) === -1;
      var label = card.querySelector('[data-bsf-card-label]');
      var srLabel = card.querySelector('[data-bsf-card-sr]');
      var quickAdd = card.querySelector('.card__quick-add');
      card.classList.toggle('bsf-not-at-branch', missing);
      if (!missing) {
        if (label) label.hidden = true;
        if (srLabel) srLabel.textContent = '';
        if (quickAdd && quickAdd.hasAttribute('data-bsf-hidden')) {
          quickAdd.hidden = false;
          quickAdd.removeAttribute('data-bsf-hidden');
        }
        return;
      }
      if (!label) {
        label = document.createElement('span');
        label.className = 'bsf-card-label';
        label.setAttribute('data-bsf-card-label', '');
        // Screen readers get the copy inside the title link below instead.
        label.setAttribute('aria-hidden', 'true');
        var media = card.querySelector('.card__media') || card;
        media.appendChild(label);
      }
      var text = (card.getAttribute('data-bsf-label') || '').replace('%STORE%', branchName());
      label.textContent = text;
      label.hidden = false;
      // The visible label sits outside every link, so a shopper moving by
      // links would only hear the product title.
      var link = card.querySelector('.card-link');
      if (link) {
        if (!srLabel) {
          srLabel = document.createElement('span');
          srLabel.className = 'visually-hidden';
          srLabel.setAttribute('data-bsf-card-sr', '');
          link.appendChild(srLabel);
        }
        srLabel.textContent = ', ' + text;
      }
      if (quickAdd && !quickAdd.hidden) {
        quickAdd.hidden = true;
        quickAdd.setAttribute('data-bsf-hidden', '');
      }
    });
  }

  // After the theme's own variant update (it resets the button for the variant).
  document.addEventListener('on:variant:change', function () { setTimeout(applyProductForms, 0); });
  document.addEventListener('on:quickbuy:after-open', function () { setTimeout(applyProductForms, 0); });

  // The variant picker upgrades after this script and resets the button on
  // connect; re-apply once it is defined (20 ms: after its own timers).
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('variant-picker').then(function () { setTimeout(applyProductForms, 20); });
  }

  // Quick-add fills its drawer AFTER opening it: watch for the form arriving.
  if (window.MutationObserver) {
    var quickAddTimer = null;
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType === 1 && (node.matches('product-form, script[data-branch-stock], product-card[data-bsf-branches]') || node.querySelector('script[data-branch-stock], product-card[data-bsf-branches]'))) {
            clearTimeout(quickAddTimer);
            quickAddTimer = setTimeout(applyProductForms, 30);
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  // --- predictive search ------------------------------------------------------
  // /search/suggest takes no filters, so predictive-search.js over-fetches and
  // hands the markup here: products the branch lacks are dropped (rows carry
  // data-bsf-branches from snippets/predictive-search-tab-panel.liquid; a row
  // without it is not indexed yet and is kept), then lists are trimmed back to
  // the theme's limit. Runs in every language - it reads metafields, not the
  // search index.
  function filterPredictive(markup, limit) {
    if (!current.slug || !markup) return markup;
    var tmpl = document.createElement('template');
    tmpl.innerHTML = markup;
    var root = tmpl.content;
    root.querySelectorAll('.predictive-search__item[data-bsf-branches]').forEach(function (li) {
      if (li.getAttribute('data-bsf-branches').split(',').indexOf(current.slug) === -1) li.remove();
    });
    if (limit > 0) {
      root.querySelectorAll('.predictive-search__list').forEach(function (list) {
        var items = list.querySelectorAll('.predictive-search__item:not(.js-submit)');
        for (var i = limit; i < items.length; i++) items[i].remove();
      });
    }
    root.querySelectorAll('.predictive-search__tabpanel').forEach(function (panel) {
      if (panel.querySelector('.predictive-search__item:not(.js-submit)')) return;
      var tab = root.querySelector('[role="tab"][aria-controls="' + panel.id + '"]');
      if (tab) tab.remove();
      panel.remove();
    });
    var tabbed = root.querySelector('tabbed-content');
    if (tabbed && !tabbed.querySelector('.predictive-search__tabpanel')) {
      var results = tabbed.parentNode;
      tabbed.remove();
      var scroller = root.querySelector('.predictive-search__scroller--suggestions');
      if (scroller) {
        scroller.classList.remove('predictive-search__scroller--suggestions');
        scroller.classList.add('predictive-search__scroller--suggestions-only');
      }
      if (!results.querySelector('.predictive-search__suggestions')) {
        var empty = document.createElement('div');
        empty.className = 'predictive-search__no-results';
        var holder = root.querySelector('[data-bsf-no-results]');
        empty.textContent = holder ? holder.getAttribute('data-bsf-no-results') : '';
        results.appendChild(empty);
      }
    }
    var wrapper = document.createElement('div');
    wrapper.appendChild(root);
    return wrapper.innerHTML;
  }

  window.BranchStorefront = {
    /** True while a branch is set, i.e. predictive results will be filtered. */
    filtering: function () { return !!current.slug; },
    filterPredictive: filterPredictive
  };

  // --- wiring -------------------------------------------------------------------

  function apply(id, source) {
    var loc = id && GID.test(id) && window.PickupLocation ? window.PickupLocation.findById(id) : null;
    if (!loc || !loc.slug) {
      current = { id: null, slug: null };
      filterCurrentPage(); // still strips a stale branch filter where listings are unfiltered
      return;
    }
    current = { id: id, slug: loc.slug };
    writeCookie(id);
    syncCart(id);
    syncFacetInputs();
    document.querySelectorAll('form[action*="/cart/add"]').forEach(stamp);
    applyProductForms();
    nameListingNotices();
    filterCurrentPage();
  }

  document.addEventListener('pickup-location:ready', function (evt) {
    apply(evt.detail && evt.detail.id, evt.detail && evt.detail.source);
  });
  document.addEventListener('pickup-location:changed', function (evt) {
    apply(evt.detail && evt.detail.id, 'manual');
  });

  // The picker may have finished before this deferred script ran.
  if (window.PickupLocation && window.PickupLocation.get()) {
    apply(window.PickupLocation.get(), window.PickupLocation.source ? window.PickupLocation.source() : 'stored');
  }

  // Never leave the grid hidden: the head guard is released here at the latest.
  setTimeout(done, 6000);
})();
